import { theme } from '@salecycle/ui-component-library';
import { EmailTrackingUnsubscribeTypes, UnsubscribeTypes } from 'core/constants/UnsubscribeConstants';
import { Selectors as Router } from 'router';
import { Selectors as BrandSelectors } from 'sections/client-management/services/brands';
import { Selectors as GroupSelectors } from 'sections/client-management/services/groups';
import { Selectors as SiteSelectors } from 'sections/client-management/services/sites';
import { State } from 'store';
import { Brand } from '../entities/brand';
import { Group } from '../entities/group';
import { Site, UnsubscribeSettings } from '../entities/site';
import { ClientConstants } from '../constants';

const canBeEmpty = ['marketingPermissions', 'campaignPriorities'];
const sessionTimeMinMillis = 60000;
const sessionTimeMaxMillis = 21600000;

export function areSitesDefined(sites: Site[]): boolean {
  if (!sites) {
    return false;
  }
  let i = sites.length;
  while (i--) {
    const emptyValues = Object.keys(sites[i]) // Get site keys
      .filter(key => !(canBeEmpty.indexOf(key) > -1)) // Filter out keys that can be empty
      // eslint-disable-next-line
      .map(key => sites[i][key]) // Get property values
      .filter(x => x === '' || (Array.isArray(x) && !x.length)); // Filter to check for empty
    if (emptyValues.length > 0) {
      return false;
    }
  }
  return true;
}

export const areSessionTimesValid = (sites: Site[]): boolean => {
  if (!sites) {
    return false;
  }

  let i = sites.length;
  while (i--) {
    if (!isSessionTimeValid(sites[i])) {
      return false;
    }
  }
  return true;
};

export const isSessionTimeValid = (site: Site): boolean => {
  if (!site || !site.sessionTime) {
    return false;
  }

  if (site.sessionTime < sessionTimeMinMillis || site.sessionTime > sessionTimeMaxMillis) {
    return false;
  }
  return true;
};

export const isConversionWindowValid = (site: Site, savedCW: number): boolean => {
  // default conversion value is set to undefined
  if (site.conversionWindow === undefined && savedCW === undefined) {
    return true;
  }

  const { CONVERSION_WINDOW_MAX, CONVERSION_WINDOW_MIN } = ClientConstants;

  if (site.conversionWindow < CONVERSION_WINDOW_MIN || site.conversionWindow > CONVERSION_WINDOW_MAX) {
    return false;
  }
  return true;
};

export const conversionWindowUpdate = (
  value: number,
  id: string,
  site: Site,
  updateMethod: (siteId: string, site: Site) => void,
) => {
  updateMethod(id, {
    ...site,
    conversionWindow: +value,
  });
};

export const removeConversionWindowFromSite = (
  id: string,
  site: Site,
  updateMethod: (siteId: string, site: Site) => void,
) => {
  updateMethod(id, {
    ...site,
    conversionWindow: undefined,
  });
};

export const sessionTimeUpdate = (
  value: number,
  unit: string,
  id: string,
  site: Site,
  updateMethod: (siteId: string, site: Site) => void,
) => {
  const msInMinute = 60 * 1000;
  const msInHours = msInMinute * 60;
  const calc = unit === 'minutes' ? msInMinute : msInHours;
  updateMethod(id, {
    ...site,
    sessionTime: +value * calc,
  });
};

export const calculateSessionTime = (value: number, unit: string) => {
  const msInMinute = 60 * 1000;
  const msInHours = msInMinute * 60;
  const calc = unit === 'minutes' ? msInMinute : msInHours;
  return +value * calc;
};

export interface EmailTrackingUnsubscribeSettingsInput {
  option?: EmailTrackingUnsubscribeTypes;
  globalEmailTrackingUnsubscribe?: string;
  emailTrackingUnsubscribeTemplate?: string;
}

/** Maps UI state to client-api `unsubscribeSettings` email-tracking fields (one template id + option). */
export function buildEmailTrackingUnsubscribeSettingsPayload(
  emailTracking?: EmailTrackingUnsubscribeSettingsInput,
): Pick<
  UnsubscribeSettings,
  'emailTrackingUnsubscribeOption' | 'globalEmailTrackingUnsubscribe' | 'emailTrackingUnsubscribeTemplate'
> | null {
  if (!emailTracking) {
    return null;
  }

  const resolvedOption: EmailTrackingUnsubscribeTypes | undefined =
    emailTracking.option ??
    (emailTracking.emailTrackingUnsubscribeTemplate
      ? 'UnsubscribeEmailTrackingTemplate'
      : emailTracking.globalEmailTrackingUnsubscribe
      ? 'GlobalUnsubscribeEmailTracking'
      : undefined);

  if (resolvedOption === 'GlobalUnsubscribeEmailTracking') {
    if (!emailTracking.globalEmailTrackingUnsubscribe) {
      if (emailTracking.option === 'GlobalUnsubscribeEmailTracking') {
        return { emailTrackingUnsubscribeOption: resolvedOption };
      }
      return null;
    }
    return {
      emailTrackingUnsubscribeOption: resolvedOption,
      globalEmailTrackingUnsubscribe: emailTracking.globalEmailTrackingUnsubscribe,
    };
  }

  if (resolvedOption === 'UnsubscribeEmailTrackingTemplate') {
    if (!emailTracking.emailTrackingUnsubscribeTemplate) {
      if (emailTracking.option === 'UnsubscribeEmailTrackingTemplate') {
        return { emailTrackingUnsubscribeOption: resolvedOption };
      }
      return null;
    }
    return {
      emailTrackingUnsubscribeOption: resolvedOption,
      emailTrackingUnsubscribeTemplate: emailTracking.emailTrackingUnsubscribeTemplate,
    };
  }

  return null;
}

export function getUnsubscribeSettings(
  unsubscribeOption: UnsubscribeTypes,
  unsubscribeURL: string,
  globalUnsubscribe: string,
  unsubscribeTemplate: string,
  emailTracking?: EmailTrackingUnsubscribeSettingsInput,
): UnsubscribeSettings {
  const settings: UnsubscribeSettings = { unsubscribeOption };
  switch (unsubscribeOption) {
    case 'UnsubscribeURL':
      settings.unsubscribeURL = unsubscribeURL;
      break;
    case 'GlobalUnsubscribe':
      settings.globalUnsubscribe = globalUnsubscribe;
      break;
    case 'UnsubscribeTemplate': {
      settings.unsubscribeTemplate = unsubscribeTemplate;
      break;
    }
    default:
      settings.globalUnsubscribe = globalUnsubscribe;
  }

  const emailTrackingPayload = buildEmailTrackingUnsubscribeSettingsPayload(emailTracking);
  if (emailTrackingPayload) {
    Object.assign(settings, emailTrackingPayload);
  }

  return settings;
}

function isSite(level: string): boolean {
  return level === 'sites' || level === 'Sites' || level === 'site' || level === 'Site';
}

function isBrand(level: string): boolean {
  return level === 'brands' || level === 'Brands' || level === 'brand' || level === 'Brand';
}

function isGroup(level: string): boolean {
  return level === 'groups' || level === 'Groups' || level === 'group' || level === 'Group';
}

export function getActiveClientFromStateAndLevel(state: State, level: string): Site | Group | Brand {
  if (isSite(level)) {
    const siteId = Router.getSiteId(state);
    return SiteSelectors.getEntity(state, siteId);
  }

  if (isBrand(level)) {
    const brandId = Router.getBrandId(state);
    return BrandSelectors.getEntity(state, brandId);
  }

  const groupId = Router.getGroupId(state);
  return GroupSelectors.getEntity(state, groupId);
}

export const getIconFromLevel = (level: string) => {
  if (level === 'MultiSite') {
    return 'users';
  } else if (isSite(level)) {
    return 'browser';
  } else if (isBrand(level)) {
    return 'users';
  } else if (isGroup(level)) {
    return 'sitemap';
  } else {
    return 'globe-americas';
  }
};

export const getIconColorFromLevel = (level: string) => {
  if (isSite(level)) {
    return theme.vars.newColor.green2;
  } else if (isBrand(level)) {
    return theme.vars.newColor.gold2;
  } else if (isGroup(level)) {
    return theme.vars.newColor.purple2;
  } else {
    return theme.vars.newColor.blue2;
  }
};

export const getClientLevelLabelFromLevel = (level: string) => {
  if (isSite(level)) {
    return 'SITE';
  } else if (isBrand(level)) {
    return 'BRAND';
  } else {
    return 'GROUP';
  }
};

export const getCurrentClientLevelId = (level: string, brandId?: string, groupId?: string, siteId?: string) => {
  switch (level) {
    case 'brands':
      return brandId;
    case 'groups':
      return groupId;
    case 'sites':
      return siteId;
    default:
      return null;
  }
};

export const areUnsubscribeSettingsValidForSites = (sites: Site[]): boolean => {
  if (!sites) {
    return false;
  }

  let i = sites.length;
  while (i--) {
    if (!areUnsubscribeSettingsValid(sites[i].unsubscribeSettings)) {
      return false;
    }
  }
  return true;
};
export const areUnsubscribeSettingsValid = (unsubscribeSettings: UnsubscribeSettings) => {
  if (
    unsubscribeSettings &&
    unsubscribeSettings.unsubscribeOption === 'UnsubscribeURL' &&
    !(unsubscribeSettings.unsubscribeURL !== 'undefined' && unsubscribeSettings.unsubscribeURL)
  ) {
    return false;
  }
  return true;
};
